import type { PagedData } from '../types/PagedData.js';

type RowRecord = Readonly<Record<string, unknown>>;

interface ValueField {
  readonly eventField: string;
  readonly rowField: string;
  readonly kind: 'string' | 'number';
}

/**
 * One board metric: its rank field plus the value field(s) it owns. The event
 * carries a non-null rank for a metric ONLY when the trading user is a member of
 * that metric's Redis board after the trade. So rank-presence is the gate: we
 * patch a metric's value + rank together iff the event carries its rank, and
 * leave the row's existing value + rank untouched otherwise. This is what
 * prevents the "$0 / rank-0 floats to the top" bug — a pure buy carries no
 * `pnlRank` (the user isn't on the realized-PnL board), so PnL is left alone.
 */
interface MetricPatch {
  readonly rankField: string;
  readonly valueFields: readonly ValueField[];
}

const FAMILY_METRICS: Readonly<Record<string, readonly MetricPatch[]>> = {
  traders: [
    {
      rankField: 'pnlRank',
      valueFields: [
        { eventField: 'totalPnl', rowField: 'totalPnl', kind: 'string' },
        { eventField: 'realizedPnl', rowField: 'realizedPnl', kind: 'string' },
      ],
    },
    {
      rankField: 'volumeRank',
      valueFields: [{ eventField: 'totalVolume', rowField: 'totalVolume', kind: 'string' }],
    },
    {
      rankField: 'tradesRank',
      valueFields: [{ eventField: 'totalTrades', rowField: 'totalTrades', kind: 'number' }],
    },
  ],
  boosters: [
    {
      rankField: 'amountRank',
      valueFields: [
        { eventField: 'totalAmountBoosted', rowField: 'totalAmountBoosted', kind: 'string' },
      ],
    },
    {
      rankField: 'boostsRank',
      valueFields: [{ eventField: 'numBoosts', rowField: 'numBoosts', kind: 'number' }],
    },
  ],
  backers: [
    {
      rankField: 'amountRank',
      valueFields: [
        { eventField: 'totalBackedNetD6', rowField: 'totalBackedNetD6', kind: 'string' },
      ],
    },
    {
      rankField: 'backsRank',
      valueFields: [{ eventField: 'numBacks', rowField: 'numBacks', kind: 'number' }],
    },
  ],
};

function asRecord(value: unknown): RowRecord | null {
  return value !== null && typeof value === 'object' ? (value as RowRecord) : null;
}

function readEventField(raw: RowRecord, key: string): unknown {
  const payload = asRecord(raw.payload);
  if (payload !== null && key in payload) return payload[key];
  return raw[key];
}

function asString(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  if (typeof value === 'bigint') return value.toString();
  return null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return Number(value);
  return null;
}

/** The unified row keys identity under `user`; `rowId` is the wallet base58. */
function rowWallet(row: RowRecord): string | null {
  const identity = asRecord(row.user);
  const walletAddress = identity?.walletAddress;
  if (typeof walletAddress === 'string' && walletAddress.length > 0) return walletAddress;
  const rowId = row.rowId;
  return typeof rowId === 'string' && rowId.length > 0 ? rowId : null;
}

/** The rank field the list is sorted by — drives the post-patch re-sort. */
function activeRankField(family: string, sortBy: unknown): string | null {
  const sort = typeof sortBy === 'string' && sortBy.length > 0 ? sortBy : null;
  if (family === 'traders') {
    if (sort === 'volume') return 'volumeRank';
    if (sort === 'trades') return 'tradesRank';
    if (sort === 'markets') return 'marketsRank';
    return 'pnlRank';
  }
  if (family === 'boosters') {
    if (sort === 'boosts') return 'boostsRank';
    if (sort === 'marketsBoosted') return 'marketsBoostedRank';
    return 'amountRank';
  }
  if (family === 'backers') {
    if (sort === 'backs') return 'backsRank';
    if (sort === 'top_market') return 'topMarketRank';
    return 'amountRank';
  }
  return null;
}

function expectedWindow(args: RowRecord): string {
  if (typeof args.window === 'string' && args.window.length > 0) return args.window;
  return 'lifetime';
}

/** Sort by the active rank ascending; rows with no rank (null) always last —
 *  mirrored on the frontend so a missing rank can never float to the top. */
function sortRowsByRank<T>(rows: readonly T[], family: string, sortBy: unknown): readonly T[] {
  const rankField = activeRankField(family, sortBy);
  if (rankField === null) return rows;
  return [...rows].sort((left, right) => {
    const leftRank = asNumber(asRecord(left)?.[rankField]);
    const rightRank = asNumber(asRecord(right)?.[rankField]);
    if (leftRank === null && rightRank === null) return 0;
    if (leftRank === null) return 1;
    if (rightRank === null) return -1;
    return leftRank - rightRank;
  });
}

function patchValue(value: unknown, kind: ValueField['kind']): string | number | null {
  return kind === 'number' ? asNumber(value) : asString(value);
}

export function applyLeaderboardRow<T>(
  prev: PagedData<T>,
  raw: Record<string, unknown>,
  args: Record<string, unknown>,
): PagedData<T>;
export function applyLeaderboardRow(
  prev: PagedData<unknown>,
  raw: Record<string, unknown>,
  args: Record<string, unknown>,
): PagedData<unknown> {
  const family = asString(readEventField(raw, 'family'));
  if (family === null || family !== asString(args.kind)) return prev;
  if (asString(readEventField(raw, 'window')) !== expectedWindow(args)) return prev;

  const metrics = FAMILY_METRICS[family];
  const wallet = asString(readEventField(raw, 'wallet'));
  if (metrics === undefined || wallet === null) return prev;

  let changed = false;
  const nextRows = prev.data.map((row) => {
    const record = asRecord(row);
    if (record === null || rowWallet(record) !== wallet) return row;

    const next: Record<string, unknown> = { ...record };
    let rowChanged = false;
    for (const metric of metrics) {
      // Gate on rank-presence: a metric absent from this event (the user isn't
      // on its board) is left untouched — never zeroed, never rank-cleared.
      const rank = asNumber(readEventField(raw, metric.rankField));
      if (rank === null) continue;
      if (next[metric.rankField] !== rank) {
        next[metric.rankField] = rank;
        rowChanged = true;
      }
      for (const vf of metric.valueFields) {
        const value = patchValue(readEventField(raw, vf.eventField), vf.kind);
        if (value !== null && next[vf.rowField] !== value) {
          next[vf.rowField] = value;
          rowChanged = true;
        }
      }
    }
    if (rowChanged) changed = true;
    return rowChanged ? next : row;
  });
  if (!changed) return prev;

  return { ...prev, data: sortRowsByRank(nextRows, family, args.sortBy) };
}
